// Header.js
import React, { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import NotificationsBoxComponent from '../notifications-box/notifications-box.component';
import Search from '../search-bar/search-bar';
import { toast } from 'react-toastify';
import { SocketContext } from '../../context/socketContext';
import { ApiContext } from '../../context/apiContext';
import { ReactComponent as Notifications } from '../../assets/notification.svg?react';
import { ReactComponent as Settings } from '../../assets/setting.svg?react';
import { Headset } from 'lucide-react';
import { openBrevoChat } from '../../utils/brevo';
import YearFilter from '../year-filter/year-filter';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { SidebarTrigger } from '../ui/sidebar';
import { UserContext } from '../../context/userContext';
import { useIsMobile } from '../../hooks/use-mobile';
// import { URL_MAP } from './app-sidebar';

// const ALL_NAV_ITEMS = [
//     ...URL_MAP.navMain,
//     ...URL_MAP.navSecondary,
//     ...URL_MAP.navTertiary,
//     ...URL_MAP.bottomNav,
//     ...URL_MAP.landlordNav,
// ];

export function SiteHeader() {
    const { user } = useContext(UserContext);
    const isMobile = useIsMobile();
    const [isCompactHeader, setIsCompactHeader] = useState(() =>
        typeof window !== 'undefined' ? window.innerWidth < 768 : isMobile
    );
    const notificationRef = useRef(null);
    const [notificationCount, setNotificationCount] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const { subscribeToNewNotifications } = useContext(SocketContext);
    const api = useContext(ApiContext);
    const navigate = useNavigate();
    const location = useLocation();
    // const activePage = location.pathname;
    // const activeNavItem = ALL_NAV_ITEMS.find((item) => activePage.startsWith(item.url));
    // const activePageName = activeNavItem ? activeNavItem.header : '';

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await api.get(`/api/notification/count`);
                setNotificationCount(data.count);
            } catch (error) {
                // toast.error(`There was an error loading notifications: ${error.message}`);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        // Function to detect outside clicks
        function handleClickOutside(event) {
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
        }

        // Bind the event listener
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [notificationRef]);

    useEffect(() => {
        // Subscribe to new chat messages
        const unsubscribe = subscribeToNewNotifications((newNotifications) => {
            setNotificationCount((prev) => prev + 1);
            toast(newNotifications.content, {
                onClick: () => {
                    const targetUrl = newNotifications?.url;
                    if (!targetUrl) return;
                    if (/^https?:\/\//i.test(targetUrl)) {
                        window.location.href = targetUrl;
                        return;
                    }
                    navigate(targetUrl);
                },
            });
        });

        return () => unsubscribe(); // Cleanup the subscription on unmount
    }, [subscribeToNewNotifications]);

    useEffect(() => {
        const media = window.matchMedia('(max-width: 767px)');
        const onChange = (event) => setIsCompactHeader(event.matches);
        setIsCompactHeader(media.matches);
        media.addEventListener('change', onChange);
        return () => media.removeEventListener('change', onChange);
    }, []);

    const toggleNotifications = () => {
        setShowNotifications((prev) => !prev);
        setNotificationCount(0);
    };

    return (
        <header className="tailwind-scope tw-z-50 tw-sticky tw-top-0 tw-flex tw-h-[var(--header-height)] tw-shrink-0 tw-items-center tw-gap-2 tw-rounded-none tw-border-b tw-bg-background tw-transition-[width,height] tw-ease-linear 2xl:tw-rounded-t-xl group-data-[collapsible=icon]/sidebar-wrapper:tw-h-[var(--header-height)]">
            <div className="tw-flex tw-w-full tw-items-center tw-gap-1 tw-px-4 tw-py-2 lg:tw-gap-2 lg:tw-px-6">
                <SidebarTrigger className="tw--ml-1" />
                {!isCompactHeader && <Separator orientation="vertical" className="tw-mx-2 !tw-h-4" />}
                {!isCompactHeader && (
                    <h1 className="tw-line-clamp-1 tw-max-w-[118px] tw-text-base tw-font-medium lg:tw-w-[250px]">
                        Hello {user?.name}{' '}
                    </h1>
                )}
                <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2">
                    <div className="tw-flex tw-items-center tw-gap-5">
                        <div className="tw-flex tw-gap-2">
                            {!isCompactHeader && <Search />}
                            {!isCompactHeader &&
                                user?.userType !== 'internal_agent' &&
                                !/insights|messaging|properties/.test(location.pathname) && (
                                <YearFilter />
                            )}
                        </div>
                        <div
                            className="tw-relative tw-flex tw-cursor-pointer"
                            ref={notificationRef}
                            onClick={toggleNotifications}
                        >
                            <Notifications />
                            {notificationCount > 0 && (
                                <div className="tw-absolute tw-right-[-5px] tw-top-[-5px] tw-flex tw-aspect-square tw-h-3 tw-items-center tw-justify-center tw-rounded-full tw-bg-red-500 tw-p-1.5 tw-py-2 tw-text-center tw-text-xs tw-font-semibold tw-text-white">
                                    {notificationCount}
                                </div>
                            )}
                            {showNotifications && <NotificationsBoxComponent />}
                        </div>
                        <button
                            type="button"
                            aria-label="Open tech support chat"
                            title="Tech Support"
                            onClick={() =>
                                openBrevoChat({
                                    context: { source: 'pm_top_header' },
                                })
                            }
                            className="tw-flex tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-gray-700 hover:tw-text-gray-900"
                        >
                            <Headset size={20} />
                        </button>
                        <div className="tw-relative tw-flex tw-cursor-pointer">
                            <Settings onClick={() => navigate(`/settings`)} />
                        </div>
                        {/* <ProfilePicture src={image} alt="Profile" /> */}
                    </div>
                </div>
            </div>
        </header>
    );
}
